import { useCallback, useEffect, useState } from 'react';
import {
  Box, Grid, IconButton, Typography, useTheme, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { OuterContainer } from '../DashboardOuterContainerPagesLayout';
import { AccountMembershipsPriceCard } from './AccountMembershipsPriceCard';
import { AccountMembershipsBenefitCard } from './AccountMembershipsBenefitCard';
import { UpgradeSuccessModal } from './UpgradeSuccessModal';
import OptimusButton from '../../../common/library/Button/Button';
import { useOptimizelyFlag } from '../../../common/hooks/useOptimizelyFlag';
import { MembershipsPageFlag } from '../../../common/OptimizelyFlagsConstants';
import { Notfound } from '../../NotFound';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';
import { UpgradePaymentModal } from './UpgradePaymentModal';
import { getStripe, isStripeConfigured } from '../../../config/stripe';
import { StripeMembershipPaymentRequest } from '../../../common/models/StripePayment';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectUpgradeStatus,
  selectUpgradeError,
  selectUpgradeErrorCode,
  selectNeedsOnboarding,
  selectMembershipPlan,
  resetUpgradeStatus,
  dismissOnboarding,
} from '../../../store/slices/subscriptionSlice';
import { getAllActivePartners } from '../../../store/thunks/dashboardThunk';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';
import { SubscriptionTier, SubscriptionStatus } from '../../../common/models/Subscription';
import { upgradeMembership } from '../../../store/thunks/subscriptionThunk';
import { TestIds } from '../../../assets/testIds';
import { MEMBERSHIP_TIER_PRICING } from '../../../common/constants';

export default function AccountMemberships() {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openExternalLink } = useNavigationUtils();
  const [isAnnual, setIsAnnual] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { trackAccountSetting, trackRequest } = useCustomEventTracking();

  const upgradeStatus = useAppSelector(selectUpgradeStatus);
  const upgradeError = useAppSelector(selectUpgradeError);
  const upgradeErrorCode = useAppSelector(selectUpgradeErrorCode);
  const memberInformation = useAppSelector(selectMemberInformation);
  const needsOnboarding = useAppSelector(selectNeedsOnboarding);
  const membershipPlan = useAppSelector(selectMembershipPlan);
  const campaignPromoCode = searchParams.get('promoCode') ?? undefined;

  /**
   * Which of the two cards on this page, if either, describes the member's plan.
   *
   * `'other'` covers every tier this page does not sell, ie. every tier above
   * Roundtable (Advantage and up). Collapsing those into `'edge'` used to label a
   * paying member as being on the free plan and offer them Roundtable as an upgrade
   * (SN-996).
   *
   * Association is kept distinct from both: it is not on the free plan, yet
   * Roundtable is a genuine upgrade for it, so it buys the same thing an Edge
   * member does.
   */
  const currentTier: 'edge' | 'am' | 'srt' | 'other' = (() => {
    if (upgradeStatus === 'success' || membershipPlan === 'srt') return 'srt';
    const subscription = memberInformation?.company?.subscription;
    const activeTier = subscription?.subscriptionStatus === SubscriptionStatus.ACTIVE ?
      subscription?.subscriptionTier :
      undefined;
    if (!activeTier || activeTier === SubscriptionTier.EDGE) return 'edge';
    if (activeTier === SubscriptionTier.AM) return 'am';
    if (activeTier === SubscriptionTier.SRT) return 'srt';
    return 'other';
  })();

  const showSuccessModal = upgradeStatus === 'success' && showPaymentModal;

  const stripePromise = getStripe();
  const stripeConfigured = isStripeConfigured();
  const {
    decision: membershipsPageDecision,
    isDecisionReady: isMembershipsPageDecisionReady,
  } = useOptimizelyFlag({
    flag: MembershipsPageFlag,
  });

  // Set the card variant based on user's current tier. Roundtable is highlighted as
  // the upgrade to buy for Edge and Association members; it is not an upgrade for a
  // member already on a tier above it.
  const edgeCardVariant = currentTier === 'edge' ? 'current' : 'default';
  const roundtableCardVariant = (() => {
    if (currentTier === 'srt') return 'current';
    return currentTier === 'edge' || currentTier === 'am' ? 'highlighted' : 'default';
  })();

  // Edge and Association members buy Roundtable here; anyone else is sent to
  // support (SN-996).
  const canBuyRoundtable = currentTier === 'edge' || currentTier === 'am';

  const selectedTier = 'srt';
  const selectedTierName = selectedTier === 'srt' ?
    'Roundtable' :
    'Edge';
  const selectedTierPrice = isAnnual ?
    MEMBERSHIP_TIER_PRICING[selectedTier].annual :
    MEMBERSHIP_TIER_PRICING[selectedTier].monthly;

  const srtPrice = isAnnual ?
    MEMBERSHIP_TIER_PRICING.srt.annual :
    MEMBERSHIP_TIER_PRICING.srt.monthly;

  const fullSrtPrice = MEMBERSHIP_TIER_PRICING.srt.monthly;

  const handlePaymentSubmit = useCallback((paymentData: StripeMembershipPaymentRequest) => {
    dispatch(upgradeMembership({
      paymentMethodId: paymentData.paymentMethodId,
      tier: selectedTier,
      billingFrequency: isAnnual ? 'annual' : 'monthly',
      zipCode: paymentData.zipCode,
      cardholderName: paymentData.cardholderName,
      promoCode: paymentData.promoCode,
      totalAmount: paymentData.totalAmount,
      trackRequest,
    }));
  }, [dispatch, selectedTier, isAnnual, trackRequest]);

  const handleSuccessModalClose = useCallback(() => {
    setShowPaymentModal(false);
    dispatch(resetUpgradeStatus());
  }, [dispatch]);

  useEffect(() => {
    if (upgradeStatus === 'success') {
      // Refresh the shared partner list after an upgrade unlocks partners. Unfiltered,
      // like every other caller: the gating is per card (SN-996).
      dispatch(getAllActivePartners());
    }
  }, [dispatch, upgradeStatus]);

  const handleBookOnboardingCall = useCallback(() => {
    trackAccountSetting('Click', 'ScheduleOnboardingCall');
    setShowPaymentModal(false);
    dispatch(resetUpgradeStatus());
    openExternalLink('https://meetings-na2.hubspot.com/duane-fowler/edge-roundtable-onboarding');
  }, [dispatch, openExternalLink, trackAccountSetting]);

  const handleDismissOnboarding = useCallback(() => {
    dispatch(dismissOnboarding());
  }, [dispatch]);

  const handleUpgradeClick = () => {
    trackAccountSetting('Click', 'UpgradeButton', {
      tier: 'srt',
      billingPeriod: isAnnual ? 'annual' : 'monthly',
    });

    if (!stripeConfigured) {
      return;
    }
    setShowPaymentModal(true);
  };

  const handleBillingPeriodChange = (billingPeriod: 'annual' | 'monthly') => {
    setIsAnnual(billingPeriod === 'annual');
    trackAccountSetting('Click', 'BillingPeriodToggle', {
      billingPeriod,
    });
  };

  const handleContactSupportClick = () => {
    navigate('/myaccount/contact');
  };

  if (isMembershipsPageDecisionReady && !membershipsPageDecision?.enabled) {
    return <Notfound />;
  }

  return (
    <OuterContainer
      sx={{
        p: `${theme.spacing(4)} ${theme.spacing(0)}`,
        [theme.breakpoints.down('md')]: {
          border: theme.spacing(0),
          padding: theme.spacing(0),
        },
      }}
    >
      <>
        {/* Page title */}
        <Typography variant="h12">
          {t('navigation.memberships')}
        </Typography>

        {/* Stripe configuration warning */}
        {!stripeConfigured && (
          <Alert severity="warning" sx={{ mb: theme.spacing(2) }}>
            <Typography variant="bodyMd">
              {t('error.stripeNotConfigured')}
            </Typography>
          </Alert>
        )}

        {/* Upgrade section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            mt: theme.spacing(4),
          }}
        >
          <Typography variant="h12" sx={{ color: theme.palette.grey[900], mb: 0.5 }}>
            {t('memberships.title')}
          </Typography>
          <Typography variant="bodyMd" sx={{ color: theme.palette.grey[600], mb: theme.spacing(1.5) }}>
            {t('memberships.subtitle')}
          </Typography>

          {/* Monthly / Annual toggle */}
          <Box>
            <OptimusButton
              buttonText={t('memberships.monthly') || 'Monthly'}
              onClick={() => handleBillingPeriodChange('monthly')}
              customVariant={!isAnnual ? 'primary' : 'secondaryGray'}
              data-testid={!isAnnual ? 'billing-monthly-selected' : 'billing-monthly-not-selected'}
              size="s"
              fullWidth={false}
              width={100}
              height={37}
              sx={{
                borderRadius: 0,
                borderTopLeftRadius: theme.spacing(0.5),
                borderBottomLeftRadius: theme.spacing(0.5),
                border: `1px solid ${theme.palette.grey[800]}`,
                borderRight: 'none',
                boxShadow: 'none',
              }}
            />
            <OptimusButton
              buttonText={t('memberships.annual') || 'Annual'}
              onClick={() => handleBillingPeriodChange('annual')}
              customVariant={isAnnual ? 'primary' : 'secondaryGray'}
              data-testid={isAnnual ? 'billing-annual-selected' : 'billing-annual-not-selected'}
              size="s"
              fullWidth={false}
              width={100}
              height={37}
              sx={{
                borderRadius: 0,
                borderTopRightRadius: theme.spacing(0.5),
                borderBottomRightRadius: theme.spacing(0.5),
                border: `1px solid ${theme.palette.grey[800]}`,
                boxShadow: 'none',
              }}
            />
          </Box>

          {/* Onboarding Banner */}
          {needsOnboarding && (
            <Box
              data-testid={TestIds.onboardingBanner.container}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                backgroundColor: theme.palette.brand.primary,
                color: theme.palette.common.white,
                borderRadius: theme.spacing(1),
                p: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
                mt: theme.spacing(2.5),
                gap: theme.spacing(2),
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography variant="bodyMd" sx={{ color: 'inherit', width: '100%' }}>
                {t('memberships.onboardingBanner.message')}
              </Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexShrink: 0, minWidth: 0,
              }}>
                <OptimusButton
                  buttonText={t('memberships.onboardingBanner.cta') || 'Schedule Your Onboarding Call'}
                  onClick={handleBookOnboardingCall}
                  customVariant="secondaryGray"
                  size="s"
                  fullWidth={false}
                  data-testid={TestIds.onboardingBanner.scheduleButton}
                  sx={{
                    backgroundColor: theme.palette.common.white,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  }}
                />
                <IconButton
                  data-testid={TestIds.onboardingBanner.dismissButton}
                  onClick={handleDismissOnboarding}
                  aria-label="Dismiss onboarding banner"
                  sx={{ color: theme.palette.common.white, p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: theme.spacing(2.5) }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Plan price cards */}
          <Grid
            container
            spacing={2.5}
            sx={{
              pt: theme.spacing(2.5),
            }}
          >
            {/* Edge Plan Card */}
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <AccountMembershipsPriceCard
                price={t('memberships.edgeTier.price')}
                tier={t('memberships.edgeTier.tier')}
                description={t('memberships.edgeTier.description')}
                features={[
                  {
                    text: t('memberships.edgeTier.feature1'),
                    prefix: 'check',
                    tooltip: t('memberships.edgeTier.feature1Tooltip'),
                  },
                  {
                    text: t('memberships.edgeTier.feature2'),
                    prefix: 'check',
                    tooltip: t('memberships.edgeTier.feature2Tooltip'),
                  },
                  {
                    text: t('memberships.edgeTier.feature3'),
                    prefix: 'check',
                    tooltip: t('memberships.edgeTier.feature3Tooltip'),
                  },
                  {
                    text: t('memberships.edgeTier.feature4'),
                    prefix: 'check',
                    tooltip: t('memberships.edgeTier.feature4Tooltip'),
                  },
                ]}
                ctaLabel={currentTier === 'edge' ? t('memberships.edgeTier.cta') : t('memberships.edgeTier.contactSupport') || 'Contact Support'}
                cardVariant={edgeCardVariant}
                onButtonClick={currentTier === 'edge' ? undefined : handleContactSupportClick}
              />
            </Grid>

            {/* Roundtable Plan Card */}
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <AccountMembershipsPriceCard
                price={isAnnual ? `$${srtPrice} / Mo (billed annually)` : `$${srtPrice} / Mo`}
                priceStrikethrough={isAnnual ? `$${fullSrtPrice}` : undefined}
                tier={t('memberships.roundtableTier.tier')}
                description={t('memberships.roundtableTier.description')}
                features={[
                  {
                    text: t('memberships.roundtableTier.feature1'),
                    prefix: 'check',
                    tooltip: t('memberships.roundtableTier.feature1Tooltip'),
                  },
                  {
                    text: t('memberships.roundtableTier.feature2'),
                    prefix: 'add',
                    tooltip: t('memberships.roundtableTier.feature2Tooltip'),
                  },
                  {
                    text: t('memberships.roundtableTier.feature3'),
                    prefix: 'add',
                    tooltip: t('memberships.roundtableTier.feature3Tooltip'),
                  },
                  {
                    text: t('memberships.roundtableTier.feature4'),
                    prefix: 'add',
                    tooltip: t('memberships.roundtableTier.feature4Tooltip'),
                  },
                  {
                    text: t('memberships.roundtableTier.feature5'),
                    prefix: 'add',
                    tooltip: t('memberships.roundtableTier.feature5Tooltip'),
                  },
                ]}
                ctaLabel={canBuyRoundtable ? t('memberships.roundtableTier.cta') : t('memberships.edgeTier.contactSupport') || 'Contact Support'}
                cardVariant={roundtableCardVariant}
                onButtonClick={canBuyRoundtable ? handleUpgradeClick : handleContactSupportClick}
                showConfetti={upgradeStatus === 'success'}
              />
            </Grid>

            {/* Benefits Section */}
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(1.25),
                  width: '100%',
                }}
              >
                <Typography variant="h4" sx={{ color: theme.palette.brand.primary, mb: 0.5 }}>
                  {t('memberships.benefits.title')}
                </Typography>
                <AccountMembershipsBenefitCard
                  icon={TrendingUpIcon}
                  title={t('memberships.benefits.benefit1.title')}
                  description={t('memberships.benefits.benefit1.description')}
                />
                <AccountMembershipsBenefitCard
                  icon={LightbulbOutlinedIcon}
                  title={t('memberships.benefits.benefit2.title')}
                  description={t('memberships.benefits.benefit2.description')}
                />
                <AccountMembershipsBenefitCard
                  icon={BoltIcon}
                  title={t('memberships.benefits.benefit3.title')}
                  description={t('memberships.benefits.benefit3.description')}
                />
                <AccountMembershipsBenefitCard
                  icon={TrackChangesIcon}
                  title={t('memberships.benefits.benefit4.title')}
                  description={t('memberships.benefits.benefit4.description')}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Payment Modal */}
        {stripeConfigured && stripePromise && (
          <Elements stripe={stripePromise}>
            <UpgradePaymentModal
              open={showPaymentModal && upgradeStatus !== 'success'}
              onClose={() => {
                if (upgradeStatus === 'loading') {
                  return;
                }
                setShowPaymentModal(false);
                dispatch(resetUpgradeStatus());
              }}
              tierName={selectedTierName}
              billingFrequency={isAnnual ? 'annual' : 'monthly'}
              amount={isAnnual ? (selectedTierPrice * 12) : selectedTierPrice}
              onSubmit={handlePaymentSubmit}
              onClearError={() => dispatch(resetUpgradeStatus())}
              initialPromoCode={campaignPromoCode}
              upgradeStatus={upgradeStatus}
              upgradeError={upgradeError}
              upgradeErrorCode={upgradeErrorCode}
            />
          </Elements>
        )}

        {/* Success Modal */}
        <UpgradeSuccessModal
          open={showSuccessModal}
          onClose={handleSuccessModalClose}
          onBookOnboardingCall={handleBookOnboardingCall}
        />
      </>
    </OuterContainer>
  );
}
